import { Robot, RobotState } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import {
  BehaviorRule,
  ConditionType,
  ActionType,
  TargetModifier,
} from '@/behaviors/BehaviorDefs';
import { ComponentType } from '@/components/ComponentDefs';
import { EventBus, GameEvents } from '@/events/EventBus';
import { GOD } from '@/config/GodMode';

// ── Re-export shared types so existing imports keep working ──
export type { BehaviorContext, EvalResult } from '@/behaviors/BehaviorTypes';
import type { BehaviorContext } from '@/behaviors/BehaviorTypes';
import type { EvalResult } from '@/behaviors/BehaviorTypes';

// ── Sub-module imports ──
import {
  setCurrentAllies, resetNavigationCaches,
  moveToward, moveAwayFrom, pursueAtDistance, patrol,
  patrolTargets, robotPaths,
} from '@/behaviors/BehaviorNavigation';

import {
  findEnemyInRange, shootAt, shootAtBase, explode,
  updateCooldown, resetCombatCaches,
} from '@/behaviors/BehaviorCombat';

import {
  passiveSupportTick,
  findNearestHarvestable, findNearestRecharger,
  findAllyLowEnergy, repairAlly, rechargeAlly, repairBase,
  spawnSupportBeam,
  resetSupportCaches,
} from '@/behaviors/BehaviorSupport';

import {
  passiveMineLayerTick, passiveJumperTick, passiveExcavatorTick,
  updateSweepLock,
  resetPassivesCaches,
} from '@/behaviors/BehaviorPassives';

// Re-export fireCooldowns for external consumers
export { fireCooldowns } from '@/behaviors/BehaviorCombat';

// ── Constant action-type sets (reused every tick — no per-call allocation) ──
const MOVEMENT_ACTIONS = new Set([
  ActionType.ADVANCE, ActionType.RETREAT, ActionType.PATROL,
  ActionType.FALL_BACK, ActionType.PROTECT_ALLY, ActionType.HOLD_POSITION,
  ActionType.CHASE, ActionType.HARVEST,
]);
const HYBRID_ACTIONS = new Set([ActionType.CHASE, ActionType.PURSUE]);

// Track which robots already showed the kamikaze war cry (once per life)
const pursueCryShown = new Set<string>();

// ════════════════════════════════════════════════════════════════
// Main tick — called once per robot per frame by WaveManager
// ════════════════════════════════════════════════════════════════

export function runBehaviorTick(
  rules: BehaviorRule[],
  ctx: BehaviorContext,
): void {
  const { robot, delta } = ctx;
  setCurrentAllies(ctx.allies);
  if (!robot.alive || robot.state === RobotState.DESTROYED) return;
  // Crashing flyers: no behavior — they're spiraling out of control
  if (robot.isCrashing) return;

  // Reset move intent each tick — if no action sets it, the robot stops
  robot.moveIntentX = 0;
  robot.moveIntentY = 0;
  robot.moveIntentSpeed = 0;

  // Passive support: self-recharge, self-repair, aura for nearby allies
  passiveSupportTick(robot, ctx.allies, delta, ctx.scene);

  // Passive mine laying: robots with MINE_LAYER drop mines on their tile
  passiveMineLayerTick(robot, ctx);

  // Passive side-jumper: push nearby enemies/allies in jumper direction
  passiveJumperTick(robot, ctx);

  // Passive excavator: harvest resources from nearby decorations
  passiveExcavatorTick(robot, ctx);

  // Energy is action-based only: no passive sensor/standby/movement drain.
  // Weapons, repair, recharge, mines, excavation etc. consume energy per action.

  // Flight is free (movement costs no energy) — no crash from energy depletion

  // Update weapon cooldowns
  updateCooldown(robot.id, delta);

  // Stunned robots can't act (passive systems above still tick)
  if (robot.isStunned) return;

  // ── Energy management: conservation mode (hysteresis) ──
  if (robot.maxEnergy > 0) {
    const ratio = robot.currentEnergy / robot.maxEnergy;
    if (robot.energySaveMode) {
      if (ratio >= 0.15) {
        robot.energySaveMode = false;
      }
    } else if (ratio <= 0.05) {
      robot.energySaveMode = true;
    }
  }

  // Sort rules by priority (lower number = higher priority)
  const sorted = [...rules].sort((a, b) => a.priority - b.priority);

  let didCombat = false;
  let didMovement = false;

  for (const rule of sorted) {
    const isMove = MOVEMENT_ACTIONS.has(rule.action);
    if (isMove && didMovement) continue;
    if (!isMove && didCombat) continue;
    if (robot.energySaveMode && !isMove) continue;

    const evalResult = evaluateCondition(rule, ctx);
    if (evalResult.matched) {
      executeAction(rule, ctx, evalResult);
      if (HYBRID_ACTIONS.has(rule.action)) {
        didMovement = true;
        didCombat = true;
      } else if (isMove) {
        didMovement = true;
      } else {
        didCombat = true;
      }
      // Track target X so fixed robots can face toward their target
      if (!isMove && evalResult.target) {
        const t = evalResult.target as { body?: { x: number }; x?: number };
        const tx = t.body?.x ?? t.x;
        if (tx !== undefined) robot.faceTargetX = tx;
      }
    }
    if (didCombat && didMovement) break;
  }

  // Fallback: if no combat action fired, attack nearest VISIBLE enemy base
  if (!didCombat && !robot.energySaveMode) {
    const aliveBases = ctx.enemyBases?.filter(b => b.alive) ?? [];
    const visibleBases = aliveBases.filter(b =>
      robot.isInSensorCone(b.x, b.y, robot.sensorRange),
    );
    if (visibleBases.length > 0) {
      const nearest = visibleBases.reduce((best, b) => {
        return robot.distanceTo(b.x, b.y) < robot.distanceTo(best.x, best.y) ? b : best;
      });
      const dt = delta / 1000;
      robot.state = RobotState.ATTACKING;
      const hasCharge = robot.hasExplosiveCharge();
      const savedSpeedFB = robot.speed;
      if (hasCharge) robot.speed *= 2;
      if (robot.canMove()) {
        if (hasCharge) {
          moveToward(robot, nearest.x, nearest.y, dt);
        } else {
          pursueAtDistance(robot, nearest.x, nearest.y, dt);
        }
        didMovement = true;
      }
      robot.speed = savedSpeedFB;
      shootAtBase(robot, nearest, ctx);
      if (hasCharge) {
        const charge = robot.getExplosiveCharge()!;
        const dist = robot.distanceTo(nearest.x, nearest.y);
        const detonationDist = Math.min(charge.def.explosionRadius * GOD.kamikazeDetonationFactor, 24);
        if (dist <= detonationDist) {
          explode(robot, ctx);
        }
      }
      robot.faceTargetX = nearest.x;
      didCombat = true;
    }
  }

  // Excavator override: if robot has excavator + resource in range, harvest takes priority over patrol/idle
  const hasExcavatorComp = robot.components.some(c => !c.broken && c.def.type === ComponentType.EXCAVATOR);
  if (hasExcavatorComp && robot.canMove() && !robot.energySaveMode) {
    const target = findNearestHarvestable(robot);
    if (target) {
      robot.state = didCombat ? RobotState.ATTACKING : RobotState.MOVING;
      moveToward(robot, target.x, target.y, delta / 1000, true);
      didMovement = true;
    }
  }

  // No combat and no movement — patrol, energy save, or idle
  if (!didCombat && !didMovement) {
    if (robot.energySaveMode && robot.canMove()) {
      const recharger = findNearestRecharger(robot, ctx.allies);
      if (recharger) {
        robot.state = RobotState.RETREATING;
        moveToward(robot, recharger.x, recharger.y, delta / 1000);
      } else {
        robot.state = RobotState.RETREATING;
        moveToward(robot, ctx.baseX, ctx.baseY, delta / 1000);
      }
    } else if (robot.hasWings() && robot.canMove()) {
      robot.state = RobotState.MOVING;
      patrol(robot, delta / 1000, ctx.baseX, ctx.baseY);
    } else {
      robot.state = RobotState.IDLE;
    }
    robot.updateDebugOverlays();
  }

  // Radar lock-on
  updateSweepLock(robot, ctx);

  // Update wheel/turbine spin animations based on current state
  robot.updateWheelSpin();
  robot.updateTurbineSpin();
}

// ════════════════════════════════════════════════════════════════
// Condition evaluation
// ════════════════════════════════════════════════════════════════

function evaluateCondition(rule: BehaviorRule, ctx: BehaviorContext): EvalResult {
  const { robot, enemies, allies } = ctx;
  const liveEnemies = enemies.filter(e => e.alive && !e.isCloaked);
  const hash = ctx.enemyHash;

  switch (rule.condition) {
    case ConditionType.ENEMY_DETECTED: {
      const detectionRange = Math.max(robot.sensorRange, GOD.eyesightRange);
      const target = findEnemyInRange(robot, liveEnemies, detectionRange, rule.targetModifier, hash);
      return { matched: target !== null, target };
    }

    case ConditionType.ENEMY_CLOSE: {
      const closeRange = rule.distanceThreshold ?? 50;
      const target = findEnemyInRange(robot, liveEnemies, closeRange, rule.targetModifier, hash);
      return { matched: target !== null, target };
    }

    case ConditionType.NO_ENEMY_DETECTED: {
      const detectionRange = Math.max(robot.sensorRange, GOD.eyesightRange);
      const any = findEnemyInRange(robot, liveEnemies, detectionRange, undefined, hash);
      return { matched: any === null };
    }

    case ConditionType.ENEMY_IN_ZONE: {
      const target = findEnemyInRange(robot, liveEnemies, robot.sensorRange, rule.targetModifier, hash);
      return { matched: target !== null, target };
    }

    case ConditionType.LOW_ENERGY: {
      const threshold = robot.maxEnergy * 0.2;
      return { matched: robot.currentEnergy < threshold };
    }

    case ConditionType.ALLY_LOW_ENERGY: {
      const lowAlly = findAllyLowEnergy(robot, allies);
      return { matched: lowAlly !== null, target: lowAlly };
    }

    case ConditionType.ALLY_NEARBY_DAMAGED: {
      const allyRange = 100;
      const damaged = allies.find(
        a => a.alive && a.id !== robot.id &&
          a.isIdle() &&
          robot.distanceTo(a.x, a.y) <= allyRange &&
          a.currentHp < a.totalHp
      );
      return { matched: damaged !== undefined, target: damaged ?? null };
    }

    case ConditionType.BASE_DAMAGED: {
      const base = ctx.playerBase;
      if (base && base.alive && base.hp < base.maxHp) {
        return { matched: true, targetX: base.x, targetY: base.y };
      }
      return { matched: false };
    }

    case ConditionType.RESOURCE_DETECTED: {
      const hasExcavator = robot.components.some(c => !c.broken && c.def.type === ComponentType.EXCAVATOR);
      if (!hasExcavator) return { matched: false };
      const res = findNearestHarvestable(robot);
      return { matched: res !== null, targetX: res?.x, targetY: res?.y };
    }

    case ConditionType.ALWAYS: {
      const target = findEnemyInRange(robot, liveEnemies, robot.sensorRange, rule.targetModifier, hash);
      return { matched: true, target };
    }

    default:
      return { matched: false };
  }
}

// ════════════════════════════════════════════════════════════════
// Action execution
// ════════════════════════════════════════════════════════════════

function executeAction(
  rule: BehaviorRule,
  ctx: BehaviorContext,
  evalResult: EvalResult,
): void {
  const { robot, delta } = ctx;
  const dt = delta / 1000;

  switch (rule.action) {
    case ActionType.SHOOT: {
      robot.state = RobotState.ATTACKING;
      const enemy = evalResult.target as Enemy | null;
      if (enemy && enemy.alive) {
        robot.faceTargetX = enemy.body.x;
        if (robot.canMove()) {
          pursueAtDistance(robot, enemy.body.x, enemy.body.y, dt);
        }
        shootAt(robot, enemy, ctx);
      }
      break;
    }

    case ActionType.ADVANCE: {
      const enemy = evalResult.target as Enemy | null;
      if (enemy && robot.canMove()) {
        robot.state = RobotState.MOVING;
        moveToward(robot, enemy.body.x, enemy.body.y, dt);
      }
      break;
    }

    case ActionType.CHASE: {
      const enemy = evalResult.target as Enemy | null;
      if (enemy && enemy.alive) {
        robot.state = RobotState.ATTACKING;
        robot.faceTargetX = enemy.body.x;
        if (robot.canMove()) {
          pursueAtDistance(robot, enemy.body.x, enemy.body.y, dt);
        }
        shootAt(robot, enemy, ctx);
      }
      break;
    }

    case ActionType.PURSUE: {
      const enemy = evalResult.target as Enemy | null;
      if (enemy && enemy.alive) {
        if (!pursueCryShown.has(robot.id)) {
          pursueCryShown.add(robot.id);
          EventBus.emit(GameEvents.PURSUE_ACTIVATED, { x: robot.x, y: robot.y, radius: robot.physicsRadius });
          if (robot.hasExplosiveCharge()) {
            EventBus.emit(GameEvents.KAMIKAZE_WAR_CRY, { x: robot.x, y: robot.y, radius: robot.physicsRadius });
          }
        }
        robot.state = RobotState.ATTACKING;
        robot.faceTargetX = enemy.body.x;
        const savedSpeed = robot.speed;
        if (robot.hasExplosiveCharge()) robot.speed *= 2;
        if (robot.canMove()) {
          moveToward(robot, enemy.body.x, enemy.body.y, dt);
        }
        shootAt(robot, enemy, ctx);
        robot.speed = savedSpeed;
        const charge = robot.getExplosiveCharge();
        if (charge) {
          const dist = robot.distanceTo(enemy.body.x, enemy.body.y);
          const detonationDist = Math.min(charge.def.explosionRadius * GOD.kamikazeDetonationFactor, 24);
          if (dist <= detonationDist) {
            explode(robot, ctx);
          }
        }
      }
      break;
    }

    case ActionType.RETREAT: {
      if (robot.canMove()) {
        robot.state = RobotState.RETREATING;
        // Retreat toward nearest base using pathfinding
        moveToward(robot, ctx.baseX, ctx.baseY, dt);
      }
      break;
    }

    case ActionType.HOLD_POSITION: {
      if (robot.hasWings() && robot.canMove()) {
        robot.state = RobotState.MOVING;
        patrol(robot, dt, ctx.baseX, ctx.baseY);
      } else {
        robot.state = RobotState.IDLE;
      }
      break;
    }

    case ActionType.PATROL: {
      if (robot.sensorRange > 0) {
        const visibleEnemies = ctx.enemies.filter(e => e.alive && !e.isCloaked);
        const detected = findEnemyInRange(robot, visibleEnemies, robot.sensorRange, rule.targetModifier, ctx.enemyHash);
        if (detected) {
          robot.state = RobotState.ATTACKING;
          if (robot.canMove()) {
            pursueAtDistance(robot, detected.body.x, detected.body.y, dt);
            patrolTargets.delete(robot.id);
            robotPaths.delete(robot.id);
          }
          shootAt(robot, detected, ctx);
          break;
        }
      }
      robot.state = RobotState.MOVING;
      if (robot.canMove()) {
        patrol(robot, dt, ctx.baseX, ctx.baseY);
      }
      break;
    }

    case ActionType.PROTECT_ALLY: {
      const ally = evalResult.target as Robot | null;
      if (ally && robot.canMove()) {
        robot.state = RobotState.SUPPORTING;
        moveToward(robot, ally.x, ally.y, dt);
      }
      break;
    }

    case ActionType.FALL_BACK: {
      if (robot.canMove()) {
        robot.state = RobotState.RETREATING;
        moveToward(robot, ctx.baseX, ctx.baseY, dt);
      }
      break;
    }

    case ActionType.EXPLODE: {
      explode(robot, ctx);
      break;
    }

    case ActionType.REPAIR_ALLY: {
      const ally = evalResult.target as Robot | null;
      if (ally && ally.alive) {
        robot.state = RobotState.SUPPORTING;
        if (repairAlly(robot, ally, dt) && ctx.scene) {
          spawnSupportBeam(ctx.scene, robot.x, robot.y, ally.x, ally.y, 'repair');
        }
      }
      break;
    }

    case ActionType.RECHARGE_ALLY: {
      const ally = evalResult.target as Robot | null;
      if (ally && ally.alive) {
        robot.state = RobotState.SUPPORTING;
        const supportRange = robot.getSupportModules().find(m => m.def.rechargeRate > 0)?.def.supportRange ?? 80;
        if (robot.canMove() && robot.distanceTo(ally.x, ally.y) > supportRange * 0.8) {
          moveToward(robot, ally.x, ally.y, dt);
        }
        if (rechargeAlly(robot, ally, dt) && ctx.scene) {
          spawnSupportBeam(ctx.scene, robot.x, robot.y, ally.x, ally.y, 'recharge');
        }
      }
      break;
    }

    case ActionType.REPAIR_BASE: {
      const base = ctx.playerBase;
      if (base && base.alive && base.hp < base.maxHp) {
        robot.state = RobotState.SUPPORTING;
        const supportRange = robot.getSupportModules().find(m => m.def.repairRate > 0)?.def.supportRange ?? 80;
        if (robot.canMove() && robot.distanceTo(base.x, base.y) > supportRange * 0.8) {
          moveToward(robot, base.x, base.y, dt);
        }
        if (repairBase(robot, base, dt) && ctx.scene) {
          spawnSupportBeam(ctx.scene, robot.x, robot.y, base.x, base.y, 'repair');
        }
      }
      break;
    }

    case ActionType.SEEK_RECHARGER: {
      const recharger = findNearestRecharger(robot, ctx.allies);
      if (recharger && robot.canMove()) {
        robot.state = RobotState.RETREATING;
        moveToward(robot, recharger.x, recharger.y, dt);
      }
      break;
    }

    case ActionType.HARVEST: {
      const tx = evalResult.targetX;
      const ty = evalResult.targetY;
      if (tx != null && ty != null && robot.canMove()) {
        robot.state = RobotState.MOVING;
        robot.faceTargetX = tx;
        moveToward(robot, tx, ty, dt, true);
      }
      break;
    }

    case ActionType.ATTACK_BASE: {
      const bases = (ctx.enemyBases?.filter(b => b.alive) ?? []).filter(b =>
        robot.isInSensorCone(b.x, b.y, robot.sensorRange),
      );
      if (bases.length === 0) break;
      const nearest = bases.reduce((best, b) => {
        const dBest = robot.distanceTo(best.x, best.y);
        const dB = robot.distanceTo(b.x, b.y);
        return dB < dBest ? b : best;
      });
      robot.state = RobotState.ATTACKING;
      const hasChargeAB = robot.hasExplosiveCharge();
      const savedSpeedAB = robot.speed;
      if (hasChargeAB) robot.speed *= 2;
      if (robot.canMove()) {
        if (hasChargeAB) {
          moveToward(robot, nearest.x, nearest.y, dt);
        } else {
          pursueAtDistance(robot, nearest.x, nearest.y, dt);
        }
      }
      robot.speed = savedSpeedAB;
      shootAtBase(robot, nearest, ctx);
      if (hasChargeAB) {
        const charge = robot.getExplosiveCharge()!;
        const dist = robot.distanceTo(nearest.x, nearest.y);
        const detonationDist = Math.min(charge.def.explosionRadius * GOD.kamikazeDetonationFactor, 24);
        if (dist <= detonationDist) {
          explode(robot, ctx);
        }
      }
      break;
    }
  }

  robot.updateDebugOverlays();
}

// ════════════════════════════════════════════════════════════════
// Cache management — exported for map transitions
// ════════════════════════════════════════════════════════════════

/** Clear all cached state — call when switching maps */
export function resetBehaviorCaches(): void {
  resetNavigationCaches();
  resetCombatCaches();
  resetSupportCaches();
  resetPassivesCaches();
  pursueCryShown.clear();
}

/** Cleanup per-robot state when a robot is destroyed */
export function cleanupRobotState(robotId: string): void {
  pursueCryShown.delete(robotId);
  // Navigation module handles robotPaths, patrolTargets, flybyState cleanup
  robotPaths.delete(robotId);
  patrolTargets.delete(robotId);
}
